import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  ScrollView,
} from "react-native";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../../firebase";
import * as Print from "expo-print";
import * as Sharing from "expo-sharing";
import { AuthContext } from "../../Context/AuthContext";
import { Ionicons } from "@expo/vector-icons";
import { useFocusEffect } from "@react-navigation/native";
import GlobalStyles from "../../components/StylenColor";
import * as FileSystem from "expo-file-system";
import { Asset } from "expo-asset";
import RNPickerSelect from "react-native-picker-select";

// Desktop web imports
import {
  DashboardLayout,
  DataTable,
  ScoreBadge,
  TrendIndicator,
  DesktopTheme,
  useDesktopDetect,
} from "../../components/web";

// Desktop Report View Component
const DesktopReportView = ({
  filteredResponses,
  latestResponses,
  selectedQuarter,
  setSelectedQuarter,
  isGeneratingPDF,
  generatePDF,
  loading,
}) => {
  // Flatten data for table display
  const tableData = [];
  filteredResponses.forEach((dept) => {
    dept.employees.forEach((emp) => {
      emp.topics.forEach((topic) => {
        const maxScore = 25; // Assuming 5 questions * 5 points
        const percentage = (topic.totalScore / maxScore) * 100;
        const prevPercentage =
          topic.previousScore !== "N/A"
            ? (topic.previousScore / maxScore) * 100
            : null;

        tableData.push({
          id: `${dept.department}-${emp.employeeIdNum}-${topic.topic}`,
          department: dept.department,
          employee: emp.userName,
          employeeId: emp.employeeIdNum,
          topic: topic.topic,
          score: Math.round(percentage),
          previousScore: prevPercentage ? Math.round(prevPercentage) : null,
          rawScore: topic.totalScore,
          date: topic.timestamp
            ? new Date(topic.timestamp).toLocaleDateString()
            : "N/A",
        });
      });
    });
  });

  const columns = [
    {
      key: "department",
      label: "Department",
      flex: 1.2,
      minWidth: 120,
      sortable: true,
    },
    {
      key: "employee",
      label: "Employee",
      flex: 1.5,
      minWidth: 150,
      sortable: true,
    },
    {
      key: "topic",
      label: "Topic",
      flex: 2,
      minWidth: 180,
      sortable: true,
    },
    {
      key: "score",
      label: "Score",
      flex: 0.8,
      minWidth: 80,
      align: "center",
      sortable: true,
      render: (value) => <ScoreBadge score={value} />,
    },
    {
      key: "trend",
      label: "Trend",
      flex: 0.6,
      minWidth: 60,
      align: "center",
      render: (_, item) => (
        <TrendIndicator current={item.score} previous={item.previousScore} />
      ),
    },
    {
      key: "date",
      label: "Date",
      flex: 1,
      minWidth: 100,
      sortable: true,
    },
  ];

  // Quarter selector for header
  const QuarterSelector = () => {
    const [hoveredQuarter, setHoveredQuarter] = useState(null);
    const quarters = ["Q1", "Q2", "Q3", "Q4"];

    return (
      <View style={desktopStyles.quarterSelector}>
        {quarters.map((qtr) => (
          <TouchableOpacity
            key={qtr}
            style={[
              desktopStyles.quarterButton,
              selectedQuarter === qtr && desktopStyles.quarterButtonActive,
              hoveredQuarter === qtr && desktopStyles.quarterButtonHover,
            ]}
            onPress={() => setSelectedQuarter(qtr)}
            onMouseEnter={
              Platform.OS === "web" ? () => setHoveredQuarter(qtr) : undefined
            }
            onMouseLeave={
              Platform.OS === "web" ? () => setHoveredQuarter(null) : undefined
            }
          >
            <Text
              style={[
                desktopStyles.quarterText,
                selectedQuarter === qtr && desktopStyles.quarterTextActive,
              ]}
            >
              {qtr}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // Export button for table header
  const ExportButton = () => (
    <TouchableOpacity
      style={desktopStyles.exportButton}
      onPress={generatePDF}
      disabled={isGeneratingPDF}
    >
      {isGeneratingPDF ? (
        <ActivityIndicator size="small" color="#fff" />
      ) : (
        <>
          <Text style={desktopStyles.exportIcon}>📥</Text>
          <Text style={desktopStyles.exportText}>Export PDF</Text>
        </>
      )}
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <DashboardLayout currentRoute="Report">
        <View style={desktopStyles.loadingContainer}>
          <ActivityIndicator size="large" color={DesktopTheme.colors.primary} />
          <Text style={desktopStyles.loadingText}>Loading responses...</Text>
        </View>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      currentRoute="Report"
      quarterSelector={<QuarterSelector />}
    >
      <View style={desktopStyles.content}>
        {/* Stats Summary */}
        <View style={desktopStyles.statsRow}>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{tableData.length}</Text>
            <Text style={desktopStyles.statLabel}>Total Responses</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>
              {filteredResponses.length}
            </Text>
            <Text style={desktopStyles.statLabel}>Departments</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>
              {new Set(tableData.map((r) => r.employeeId)).size}
            </Text>
            <Text style={desktopStyles.statLabel}>Contributors</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>
              {tableData.length > 0
                ? Math.round(
                    tableData.reduce((sum, r) => sum + r.score, 0) /
                      tableData.length
                  )
                : 0}
              %
            </Text>
            <Text style={desktopStyles.statLabel}>Average Score</Text>
          </View>
        </View>

        {/* Data Table */}
        <View style={desktopStyles.tableWrapper}>
          <DataTable
            data={tableData}
            columns={columns}
            searchable={true}
            searchPlaceholder="Search by employee, department, or topic..."
            searchKeys={["employee", "department", "topic"]}
            emptyMessage={`No responses found for ${selectedQuarter}`}
            headerRight={<ExportButton />}
          />
        </View>
      </View>
    </DashboardLayout>
  );
};

const AllResponsesScreen = () => {
  const { companyId, companyName } = useContext(AuthContext);
  const [latestResponses, setLatestResponses] = useState([]);
  const [filteredResponses, setFilteredResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const { width } = useWindowDimensions();
  const { isDesktopWeb } = useDesktopDetect();
  const isLegacyDesktopWeb = Platform.OS === "web" && width > 768;
  const [logoBase64, setLogoBase64] = useState(null);

  useEffect(() => {
    fetchLatestResponses();
  }, []);

  useEffect(() => {
    groupByDepartmentAndEmployee(selectedQuarter);
  }, [selectedQuarter, latestResponses]);

  useFocusEffect(
    useCallback(() => {
      fetchLatestResponses();
    }, [])
  );

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const fetchLatestResponses = async () => {
    try {
      setLoading(true);
      const responsesRef = collection(
        db,
        `companies/${companyId}/questionnaireResponses`
      );
      const querySnapshot = await getDocs(responsesRef);

      const allResponses = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        timestamp: doc.data().timestamp.toDate(),
      }));

      const prevScoreMap = new Map();
      allResponses.forEach((res) => {
        const key = `${res.employeeIdNum}-${res.topic}-${res.quarter}`;
        prevScoreMap.set(key, res);
      });

      const quarters = ["Q1", "Q2", "Q3", "Q4"];
      const getPrevQuarter = (qtr) => {
        const index = quarters.indexOf(qtr);
        return index > 0 ? quarters[index - 1] : null;
      };

      const latestWithPrevious = allResponses.map((res) => {
        const prevQuarter = getPrevQuarter(res.quarter);
        let previousScore = "N/A";

        if (prevQuarter) {
          const prevKey = `${res.employeeIdNum}-${res.topic}-${prevQuarter}`;
          previousScore = prevScoreMap.get(prevKey)?.totalScore ?? "N/A";
        }

        return {
          ...res,
          previousScore,
        };
      });

      setLatestResponses(latestWithPrevious);
    } catch (error) {
      console.error("Error fetching responses:", error);
      showAlert("Please contact us", "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const groupByDepartmentAndEmployee = (quarter) => {
    const grouped = {};

    latestResponses
      .filter((res) => res.quarter === quarter)
      .forEach((res) => {
        const dept = res.department;
        const empKey = `${res.employeeIdNum}-${res.userName}`;

        if (!grouped[dept]) grouped[dept] = {};

        if (!grouped[dept][empKey]) {
          grouped[dept][empKey] = {
            employeeIdNum: res.employeeIdNum,
            userName: res.userName,
            topics: [],
          };
        }

        grouped[dept][empKey].topics.push({
          topic: res.topic,
          totalScore: res.totalScore,
          previousScore: res.previousScore,
          timestamp: res.timestamp,
        });
      });

    const structured = Object.keys(grouped).map((dept) => ({
      department: dept,
      employees: Object.values(grouped[dept]),
    }));

    setFilteredResponses(structured);
  };

  useEffect(() => {
    const fetchLogoFromDB = async () => {
      try {
        const snapshot = await getDocs(collection(db, "sowin_image"));
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setLogoBase64(data.image);
        }
      } catch (err) {
        console.error("Failed to fetch logo:", err);
      }
    };

    fetchLogoFromDB();
  }, []);

  const generatePDF = async () => {
    const responsesForQuarter = latestResponses.filter(
      (res) => res.quarter === selectedQuarter
    );

    if (responsesForQuarter.length === 0) {
      showAlert("Not Available", "No responses available in this quarter");
      return;
    }

    try {
      setIsGeneratingPDF(true);

      const logoUrl = logoBase64 || "data:image/png;base64,...";

      const coverPage = `
        <div style="page-break-after: always; padding: 40px; height: calc(100vh - 60px); box-sizing: border-box;">
          <div style="
            height: 100%;
            border: 4px solid #003366;
            border-radius: 12px;
            padding: 60px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          ">
            <img src="${logoUrl}" style="width: 220px;" />
            <h1 style="margin-top: 20px; color: #003366; font-size: 32px;">${companyName}</h1>
            <h2 style="color: #003366; font-size: 28px;">ESG Performance Report</h2>
            <h3 style="color: #003366; font-size: 26px;">Quarter: ${selectedQuarter}</h3>
            <p style="margin-top: 100px; font-size: 22px;">Generated on: ${new Date().toLocaleString()}</p>
          </div>
        </div>
      `;

      const topicAnchors = [];
      responsesForQuarter.forEach((res, i) =>
        topicAnchors.push({ anchorId: `topic-${i}`, topic: res.topic })
      );

      const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) {
          result.push(arr.slice(i, i + size));
        }
        return result;
      };

      const indexChunks = chunkArray(topicAnchors, 20);
      const indexPage = indexChunks
        .map((chunk, i) => {
          const showTitle =
            i === 0
              ? `<h1 style="text-align:center;color:#003366;">Index</h1>`
              : "";
          return `
            <div style="page-break-after: always; padding: 30px; padding-top: ${
              i === 0 ? "0px" : "60px"
            };">
              ${showTitle}
              <table style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 16px;">
                <thead>
                  <tr>
                    <th style="border: 1px solid #ddd; padding: 10px; background-color: #003366; color: white; text-align: left;">Sr. No.</th>
                    <th style="border: 1px solid #ddd; padding: 10px; background-color: #003366; color: white; text-align: left;">Topic Name</th>
                  </tr>
                </thead>
                <tbody>
                  ${chunk
                    .map(
                      (item, idx) => `
                    <tr>
                      <td style="border: 1px solid #ddd; padding: 10px;">${
                        i * 20 + idx + 1
                      }</td>
                      <td style="border: 1px solid #ddd; padding: 10px;">
                        <a href="#${
                          item.anchorId
                        }" style="color: #003366; text-decoration: underline;">${
                        item.topic
                      }</a>
                      </td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
              </table>
            </div>
          `;
        })
        .join("");

      const responseHtml = responsesForQuarter
        .map((res, index) => {
          const anchorId = `topic-${index}`;
          const responseChunks = [];

          for (let i = 0; i < res.responses.length; i += 7) {
            const chunk = res.responses.slice(i, i + 7);
            const chunkHtml = chunk
              .map((resp, j) => {
                const questionIndex = i + j + 1;
                return `
                  <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;">Q${questionIndex}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${resp.question}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${resp.selectedOption}</td>
                  </tr>
                `;
              })
              .join("");

            responseChunks.push(`
              <div style="page-break-before: ${
                i === 0 ? "auto" : "always"
              }; padding-top: ${i === 0 ? "0" : "60px"};">
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 16px;">
                  <thead>
                    <tr>
                      <th style="border: 1px solid #ddd; padding: 8px; background-color: #003366; color: white;">Q.No</th>
                      <th style="border: 1px solid #ddd; padding: 8px; background-color: #003366; color: white;">Question</th>
                      <th style="border: 1px solid #ddd; padding: 8px; background-color: #003366; color: white;">Selected Option</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${chunkHtml}
                  </tbody>
                </table>
              </div>
            `);
          }

          return `
            <div id="${anchorId}" style="page-break-before: ${
            index === 0 ? "auto" : "always"
          }; padding: 30px;">
              <h1 style="text-align: center; color: #003366; margin-bottom: 5px;">Performance Report (Q${selectedQuarter.replace(
                "Q",
                ""
              )})</h1>
              <h2 style="text-align: center; color: #003366; margin-top: 0;">${
                res.topic
              }</h2>
              <hr style="height: 1px; background-color: #ccc; border: none; margin: 10px 0;" />
              <p><b>Name:</b> ${res.userName}</p>
              <p><b>Employee ID:</b> ${res.employeeIdNum}</p>
              <p><b>Department:</b> ${res.department}</p>
              <p><b>Total Score:</b> ${res.totalScore} / ${
            res.responses.length * 5
          }</p>
              <p><b>Percentage Score:</b> ${Math.round(
                (res.totalScore / (res.responses.length * 5)) * 100
              )}%</p>
              <p><b>Quarter:</b> ${res.quarter || "N/A"}</p>
              <p><b>Date:</b> ${
                res.timestamp ? res.timestamp.toLocaleString() : "N/A"
              }</p>
              <div style="margin-top: 20px;">
                <h2 style="color: #003366;">Responses:</h2>
                ${responseChunks.join("")}
              </div>
            </div>
          `;
        })
        .join("");

      const htmlContent = `
        <html>
          <head>
            <title>${companyName} ${selectedQuarter}- ESG Performance Report</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 10px;
                margin-bottom: 120px;
                position: relative;
              }
              h1, h2, h3 {
                color: #003366;
              }
              p {
                font-size: 16px;
                line-height: 1.5;
              }
              table {
                font-size: 16px;
              }
              footer {
                position: fixed;
                bottom: 20px;
                left: 0;
                right: 0;
                text-align: center;
                font-size: 12px;
                color: #555;
              }
              .watermark {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-45deg);
                font-size: 60px;
                font-weight: bold;
                font-family: Lato, sans-serif;
                color: rgba(0, 0, 0, 0.05);
                z-index: 0;
                pointer-events: none;
                white-space: nowrap;
              }

              @media print {
              @page {
                  margin-left: 0.2in;
                  margin-right: 0.2in;
                  margin-top: 0in;
                  margin-bottom: 0in;
                }
                body {
                  margin: 0 !important;
                  -webkit-print-color-adjust: exact !important;
                }
                thead th {
                  background-color: #003366 !important;
                  -webkit-print-color-adjust: exact;
                  color: white !important;
                }
                hr {
                  border: none;
                  height: 1px;
                  background-color: #003366 !important;
                  -webkit-print-color-adjust: exact;
                }
                .watermark {
                  color: rgba(0, 0, 0, 0.05) !important;
                  -webkit-print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="watermark">SOWIN ENTERPRISES</div>
            ${coverPage}
            ${indexPage}
            ${responseHtml}
            <footer>
              Report for <b>${companyName}</b> – <b>${selectedQuarter}</b> generated on <b>${new Date().toLocaleString()}</b>
            </footer>
          </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Popup blocked. Please allow popups for this site.");
        return;
      }

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 700);
    } catch (error) {
      console.error("Print generation error:", error);
      showAlert("Please contact us", "Failed to generate report");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const sharePDF = async (uri) => {
    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(uri);
    } else {
      showAlert("Sharing not supported on this device");
    }
  };

  // Render desktop version
  if (isDesktopWeb) {
    return (
      <DesktopReportView
        filteredResponses={filteredResponses}
        latestResponses={latestResponses}
        selectedQuarter={selectedQuarter}
        setSelectedQuarter={setSelectedQuarter}
        isGeneratingPDF={isGeneratingPDF}
        generatePDF={generatePDF}
        loading={loading}
      />
    );
  }

  // Original mobile version
  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View
        style={[
          isLegacyDesktopWeb ? styles.headerRowWeb : null,
          { marginBottom: 10 },
        ]}
      >
        <Text
          style={[
            styles.title,
            GlobalStyles.titleTxtOffset,
            isLegacyDesktopWeb && styles.titleWeb,
          ]}
        >
          Latest Company Report
        </Text>

        <View style={isLegacyDesktopWeb ? styles.rightControls : null}>
          <Text style={styles.quarterLabel}>Select Quarter:</Text>

          <View style={styles.pickerContainer}>
            <RNPickerSelect
              onValueChange={(value) => setSelectedQuarter(value)}
              value={selectedQuarter}
              useNativeAndroidPickerStyle={false}
              placeholder={{}}
              style={{
                inputIOS: {
                  fontSize: GlobalStyles.fontSize.topTitle,
                  fontFamily: GlobalStyles.GlobalFont.fontFamily,
                  color: "#333",
                  paddingVertical: 10,
                },
                inputAndroid: {
                  fontSize: GlobalStyles.fontSize.topTitle,
                  fontFamily: GlobalStyles.GlobalFont.fontFamily,
                  color: "#333",
                },
                placeholder: {
                  color: "#888",
                },
                iconContainer: {
                  top: 5,
                  right: 5,
                },
              }}
              items={[
                { label: "Q1 (Jan-Mar)", value: "Q1" },
                { label: "Q2 (Apr-Jun)", value: "Q2" },
                { label: "Q3 (Jul-Sep)", value: "Q3" },
                { label: "Q4 (Oct-Dec)", value: "Q4" },
              ]}
              Icon={
                isLegacyDesktopWeb
                  ? null
                  : () => (
                      <Ionicons name="chevron-down" size={24} color="gray" />
                    )
              }
            />
          </View>

          {isLegacyDesktopWeb && (
            <TouchableOpacity
              style={[styles.downloadButtonWeb, GlobalStyles.btnBgShadow]}
              onPress={generatePDF}
              disabled={isGeneratingPDF}
            >
              {isGeneratingPDF ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <Ionicons name="download-outline" size={25} color="white" />
                  <Text
                    style={[
                      styles.buttonText,
                      GlobalStyles.btntxtoffset,
                      isLegacyDesktopWeb && styles.buttonTextWeb,
                    ]}
                  >
                    Download Report
                  </Text>
                </>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>

      <FlatList
        data={filteredResponses}
        keyExtractor={(item) => item.department}
        renderItem={({ item }) => (
          <View style={{ marginBottom: 20 }}>
            {isLegacyDesktopWeb ? (
              <View style={styles.departmentWrapperWeb}>
                <View style={styles.hrLine} />
                <Text
                  style={[styles.departmentHeader, styles.departmentHeaderWeb]}
                >
                  {item.department}
                </Text>
                <View style={styles.hrLine} />
              </View>
            ) : (
              <Text style={styles.departmentHeader}>{item.department}</Text>
            )}
            <View style={isLegacyDesktopWeb ? styles.cardRowWeb : null}>
              {item.employees.map((emp) => (
                <View
                  key={emp.employeeIdNum}
                  style={[
                    styles.responseCard,
                    GlobalStyles.btnBgShadow,
                    isLegacyDesktopWeb && styles.responseCardWeb,
                  ]}
                >
                  <Text
                    style={[
                      styles.employeeName,
                      isLegacyDesktopWeb && styles.employeeNameWeb,
                    ]}
                  >
                    {emp.userName} ({emp.employeeIdNum})
                  </Text>
                  {emp.topics.map((topic, index) => (
                    <View key={index} style={styles.topicContainer}>
                      <Text
                        style={[
                          styles.details,
                          isLegacyDesktopWeb && styles.detailsWeb,
                        ]}
                      >
                        Topic: {topic.topic}
                      </Text>
                      <Text
                        style={[
                          styles.details,
                          isLegacyDesktopWeb && styles.detailsWeb,
                        ]}
                      >
                        Latest Score: {topic.totalScore}
                      </Text>
                      <Text
                        style={[
                          styles.details,
                          isLegacyDesktopWeb && styles.detailsWeb,
                        ]}
                      >
                        Previous Score: {topic.previousScore}
                      </Text>
                      <Text
                        style={[
                          styles.details,
                          isLegacyDesktopWeb && styles.detailsWeb,
                        ]}
                      >
                        Date: {topic.timestamp.toLocaleString()}
                      </Text>
                      <View style={styles.topicDivider} />
                    </View>
                  ))}
                </View>
              ))}
            </View>
          </View>
        )}
      />

      {!isLegacyDesktopWeb && (
        <TouchableOpacity
          style={[styles.downloadButton, GlobalStyles.btnBgShadow]}
          onPress={generatePDF}
          disabled={isGeneratingPDF}
        >
          {isGeneratingPDF ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Ionicons name="download-outline" size={25} color="white" />
              <Text style={[styles.buttonText, GlobalStyles.btntxtoffset]}>
                Download Report
              </Text>
            </>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
};

// Desktop styles
const desktopStyles = StyleSheet.create({
  content: {
    flex: 1,
    padding: DesktopTheme.spacing.xl,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  loadingText: {
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: DesktopTheme.colors.textMuted,
    marginTop: DesktopTheme.spacing.md,
  },

  // Stats row
  statsRow: {
    flexDirection: "row",
    gap: DesktopTheme.spacing.lg,
    marginBottom: DesktopTheme.spacing.xl,
  },
  statCard: {
    flex: 1,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.lg,
    alignItems: "center",
    ...DesktopTheme.shadows.card,
  },
  statValue: {
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontSize: DesktopTheme.typography.fontSize.h2,
    color: DesktopTheme.colors.textPrimary,
  },
  statLabel: {
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontSize: DesktopTheme.typography.fontSize.small,
    color: DesktopTheme.colors.textMuted,
    marginTop: DesktopTheme.spacing.xs,
  },

  // Table
  tableWrapper: {
    flex: 1,
    minHeight: 400,
  },

  // Quarter selector
  quarterSelector: {
    flexDirection: "row",
    backgroundColor: DesktopTheme.colors.contentBg,
    borderRadius: DesktopTheme.borderRadius.md,
    padding: 4,
  },
  quarterButton: {
    paddingVertical: DesktopTheme.spacing.sm,
    paddingHorizontal: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.sm,
  },
  quarterButtonActive: {
    backgroundColor: DesktopTheme.colors.primary,
  },
  quarterButtonHover: {
    backgroundColor: DesktopTheme.colors.border,
  },
  quarterText: {
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontSize: DesktopTheme.typography.fontSize.small,
    color: DesktopTheme.colors.textSecondary,
  },
  quarterTextActive: {
    color: "#fff",
  },

  // Export button
  exportButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: DesktopTheme.colors.primary,
    paddingVertical: DesktopTheme.spacing.sm,
    paddingHorizontal: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.md,
  },
  exportIcon: {
    fontSize: 16,
    marginRight: DesktopTheme.spacing.xs,
  },
  exportText: {
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontSize: DesktopTheme.typography.fontSize.small,
    color: "#fff",
  },
});

// Mobile styles (original)
const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
  },
  title: {
    color: GlobalStyles.GlobalBg.headerColor,
    fontSize: GlobalStyles.fontSize.xl,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
    marginBottom: 10,
    paddingVertical: 1,
  },
  responseCard: {
    padding: 15,
    backgroundColor: "#effdee",
    marginBottom: 10,
    borderRadius: 8,
    elevation: 2,
  },
  cardRowWeb: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: 16,
  },
  responseCardWeb: {
    flexBasis: "32%",
    maxWidth: "32%",
    minWidth: 280,
  },
  employeeName: {
    fontSize: GlobalStyles.fontSize.sm,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 2,
    paddingVertical: 1,
  },
  details: {
    fontSize: GlobalStyles.fontSize.xss,
    color: "#555",
    marginBottom: 2,
    paddingVertical: 1,
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  downloadButton: {
    flexDirection: "row",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    padding: 12,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 10,
  },
  buttonText: {
    color: "white",
    fontSize: GlobalStyles.fontSize.btn,
    marginLeft: 10,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  departmentHeader: {
    fontSize: GlobalStyles.fontSize.lg,
    marginVertical: 10,
    marginLeft: 5,
    color: GlobalStyles.GlobalBg.headerColor,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  topicContainer: {
    marginTop: 8,
  },
  topicDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    marginVertical: 6,
  },
  headerRowWeb: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  titleWeb: {
    textAlign: "left",
    flex: 1,
    fontSize: GlobalStyles.fontSize.md,
  },
  rightControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 8,
  },
  quarterLabel: {
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    fontSize: GlobalStyles.fontSize.sm,
    color: "#333",
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    backgroundColor: "#effdee",
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === "ios" ? 10 : 0,
    minWidth: 140,
  },
  downloadButtonWeb: {
    flexDirection: "row",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  employeeNameWeb: {
    fontSize: GlobalStyles.fontSize.xss,
  },
  detailsWeb: {
    fontSize: GlobalStyles.fontSize.xsss - 1,
  },
  departmentHeaderWeb: {
    fontSize: GlobalStyles.fontSize.md,
    alignContent: "center",
    alignSelf: "center",
  },
  buttonTextWeb: {
    fontSize: GlobalStyles.fontSize.xs,
  },
  hrLine: {
    borderBottomWidth: 2,
    borderBottomColor: "#254128",
    marginVertical: 10,
  },
  departmentWrapperWeb: {
    marginTop: 10,
    marginBottom: 10,
  },
});

export default AllResponsesScreen;
